import React, { type ElementType, forwardRef, type Key } from 'react';

import { useIsomorphicLayoutEffect } from '../../../hooks/useIsomorphicLayoutEffect';
import { useMeasureElement } from '../../../hooks/useMeasureElement';
import { useMergeRefs } from '../../../hooks/useMergeRefs';
import hasDOMMeasurements from '../../../utils/hasDOMMeasurements';
import { useOverflowContext } from '../Overflow.context';

export type OverflowItemProps = {
    id: Key;
    as?: ElementType;
    alwaysVisible?: boolean;
} & Omit<React.HTMLAttributes<HTMLElement>, 'id'>;

export const OverflowItem = forwardRef<HTMLElement, OverflowItemProps>(
    ({ children, id, as: Component = 'div', alwaysVisible, style, ...props }, ref) => {
        const { setItemData, isItemVisible } = useOverflowContext();
        const [measureRef, { width }] = useMeasureElement();
        const mergedRef = useMergeRefs([measureRef, ref]);
        const isVisible = isItemVisible(id);

        const overflowStyles =
            isVisible && (width > 0 || !hasDOMMeasurements())
                ? { opacity: 1 }
                : {
                      opacity: 0,
                      height: 0,
                      overflowY: 'hidden',
                      pointerEvents: 'none',
                      position: 'absolute'
                  };

        useIsomorphicLayoutEffect(() => {
            setItemData(id, { width, alwaysVisible });
            return () => setItemData(id, null);
        }, [id, setItemData, width, alwaysVisible]);

        return (
            <Component
                ref={mergedRef}
                {...props}
                id={`overflow-item-${String(id)}`}
                style={{ ...style, ...overflowStyles }}
                aria-hidden={isVisible ? undefined : 'true'}
            >
                {children}
            </Component>
        );
    }
);
OverflowItem.displayName = 'OverflowItem';
